import { createFileRoute, Link } from '@tanstack/react-router';
import { formatDistanceToNowStrict } from 'date-fns';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';
import { Label } from '@apps/web/components/ui/label';
import { Spinner } from '@apps/web/components/ui/spinner';
import { Switch } from '@apps/web/components/ui/switch';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { CaseTrendChart } from '@apps/web/routes/_app/admin/voice-evals/case_trend_chart';
import {
    formatPercent,
    shortSha,
    SourceBadge,
} from '@apps/web/routes/_app/admin/voice-evals/shared';
import { useVoiceEvalCaseHistory } from '@apps/web/swr_data_fetching/admin_voice_eval_queries';
import { type EvalCaseHistoryItem } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/voice-evals/cases')({
    head: () => ({
        meta: [{ title: 'Eval Case History | Phoebe Admin' }],
    }),
    component: VoiceEvalCaseHistoryPage,
    validateSearch: z.object({
        registry_id: z.string(),
    }),
});

function HistoryResultBadge({ item }: { item: EvalCaseHistoryItem }) {
    if (item.flaky) {
        return (
            <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                FLAKY
            </Badge>
        );
    }
    if (item.success) {
        return (
            <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                PASS
            </Badge>
        );
    }
    return (
        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">FAIL</Badge>
    );
}

function VoiceEvalCaseHistoryPage() {
    const { registry_id: registryId } = Route.useSearch();
    const { data, isLoading } = useVoiceEvalCaseHistory(registryId);
    const [includeOtherRuns, setIncludeOtherRuns] = useState(false);

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center">
                <Spinner size="xl" tone="muted" />
            </div>
        );
    }

    const entry = data?.registry_entry ?? null;
    const items = data?.items ?? [];
    const entryCaseName = entry?.['case_name'];
    const caseName =
        typeof entryCaseName === 'string'
            ? entryCaseName
            : (registryId.split(':').pop() ?? registryId);

    return (
        <div className="flex h-full flex-col gap-4 overflow-auto p-4">
            <div>
                <Button variant="ghost" size="sm" asChild={true}>
                    <Link to="/admin/voice-evals/catalog">
                        <ArrowLeft className="mr-1 h-4 w-4" />
                        Catalog
                    </Link>
                </Button>
            </div>
            <div>
                <h1 className="font-mono text-lg font-semibold">{caseName}</h1>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                    {entry?.['use_case'] !== undefined && (
                        <Badge variant="outline">
                            {String(entry['use_case'])}
                        </Badge>
                    )}
                    {entry?.['harness'] !== undefined &&
                        entry['harness'] !== null && (
                            <Badge variant="secondary">
                                {String(entry['harness'])}
                            </Badge>
                        )}
                    {entry?.['kind'] !== undefined &&
                        entry['kind'] !== null && (
                            <Badge variant="secondary">
                                {String(entry['kind'])}
                            </Badge>
                        )}
                    {typeof entry?.['status'] === 'string' && (
                        <Badge
                            variant="outline"
                            className={
                                entry['status'] === 'known_gap'
                                    ? 'text-amber-700'
                                    : 'text-blue-700'
                            }
                        >
                            {entry['status']}
                        </Badge>
                    )}
                    {typeof entry?.['production_source_url'] === 'string' && (
                        <a
                            href={entry['production_source_url']}
                            target="_blank"
                            rel="noreferrer"
                            className="text-muted-foreground flex items-center gap-1 text-xs underline underline-offset-2"
                        >
                            production source
                            <ExternalLink className="h-3 w-3" />
                        </a>
                    )}
                    <span className="text-muted-foreground ml-1 font-mono text-xs">
                        {registryId}
                    </span>
                </div>
            </div>
            <Card>
                <CardHeader className="flex-row items-center justify-between space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">
                        Pass rate over time
                    </CardTitle>
                    <div className="flex items-center gap-2">
                        <Switch
                            id="include-other-runs"
                            checked={includeOtherRuns}
                            onCheckedChange={setIncludeOtherRuns}
                        />
                        <Label
                            htmlFor="include-other-runs"
                            className="text-sm font-normal"
                        >
                            Include branch / dirty runs
                        </Label>
                    </div>
                </CardHeader>
                <CardContent>
                    <CaseTrendChart
                        items={items}
                        includeOtherRuns={includeOtherRuns}
                    />
                </CardContent>
            </Card>
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">
                        Recent results
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {items.length === 0 ? (
                        <div className="text-muted-foreground py-8 text-center text-sm">
                            No recorded results for this case yet.
                        </div>
                    ) : (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Result</TableHead>
                                    <TableHead>When</TableHead>
                                    <TableHead>Pass rate</TableHead>
                                    <TableHead>Source</TableHead>
                                    <TableHead>Git</TableHead>
                                    <TableHead />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {items.map((item) => (
                                    <TableRow key={item.case_result_id}>
                                        <TableCell>
                                            <HistoryResultBadge item={item} />
                                        </TableCell>
                                        <TableCell className="text-xs whitespace-nowrap">
                                            {formatDistanceToNowStrict(
                                                new Date(
                                                    item.finished_at ??
                                                        item.created_at,
                                                ),
                                                { addSuffix: true },
                                            )}
                                        </TableCell>
                                        <TableCell className="text-xs">
                                            {formatPercent(item.pass_rate)}
                                        </TableCell>
                                        <TableCell>
                                            <SourceBadge source={item.source} />
                                        </TableCell>
                                        <TableCell>
                                            <span className="flex items-center gap-1.5 font-mono text-xs">
                                                {item.git_branch ?? 'unknown'}@
                                                {shortSha(item.git_commit)}
                                                {item.git_is_dirty && (
                                                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                                                        dirty
                                                    </Badge>
                                                )}
                                                {item.worktree_name &&
                                                    item.worktree_name !==
                                                        'phoebe' && (
                                                        <Badge
                                                            variant="secondary"
                                                            className="font-mono text-xs"
                                                        >
                                                            {item.worktree_name}
                                                        </Badge>
                                                    )}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Link
                                                to="/admin/voice-evals/runs/$runId/cases/$caseResultId"
                                                params={{
                                                    runId: item.run_id,
                                                    caseResultId:
                                                        item.case_result_id,
                                                }}
                                                className="text-xs underline underline-offset-2"
                                            >
                                                View detail
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
